import React, { useState, useRef, useEffect } from 'react'

import {
  Badge, Button, Icon, FilterIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { object, string, bool } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import getActiveFilterCount from './getActiveFilterCount'

let RemainingFilters

function LazyRemainingFilters({
  locale = 'en',
  selectedFilters = EMPTY_OBJECT,
  subscriptionFlowEnabled = false,
}) {
  const [isLoaded, setIsLoaded] = useState(false)
  const buttonRef = useRef(null)
  const currentBreakpoint = useBreakpoint()

  const activeFilterCount = getActiveFilterCount({ selectedFilters, currentBreakpoint, subscriptionFlowEnabled })

  // Fetch and evaluate the filters module while the browser is idle, so the first tap mounts it
  // from cache instead of loading it inside the tap. The trigger itself is only swapped on that
  // tap: swapping it here would move the filter bar after load.
  useEffect(() => {
    if (RemainingFilters) {
      return undefined
    }

    const preload = () => {
      import('./RemainingFilters').then((module) => {
        RemainingFilters = RemainingFilters || module.default
      })
    }
    const idleId = typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(preload, { timeout: 5000 })
      : window.setTimeout(preload, 2000)

    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId)
      } else {
        window.clearTimeout(idleId)
      }
    }
  }, [])

  function imporModules() {
    import('./RemainingFilters').then((module) => {
      RemainingFilters = module.default
      setIsLoaded(true)

      // dirty hackery here
      if (buttonRef && buttonRef.current) {
        buttonRef.current.children[0].click()
      }
    })
  }

  function handleFirstClick() {
    imporModules()
  }

  function getTrigger(onClick = null, isActive = false) {
    return (
      <div className="cgt-ml-20 wayfinder__remaining-filters" ref={buttonRef}>
        <Button
          title="Filters"
          aria-label={i18n.t('listingPage.filters.remaining.label', { ns: 'rails', lng: locale })}
          icon={<Icon svgComponent={FilterIcon} notSpaced />}
          type="secondary"
          variant="only-icon"
          customClass="remaining-filters__button"
          onClick={onClick}
          isActive={isActive}
        />
      </div>
    )
  }

  if (isLoaded && !!RemainingFilters) {
    return (
      <RemainingFilters getTrigger={getTrigger} />
    )
  }

  // Render the active-filter badge from context even before the heavy RemainingFilters module is
  // imported. The badge otherwise only exists inside that lazily-loaded module, so it vanished
  // whenever this wrapper mounted fresh (e.g. a desktop<->mobile resize swaps the nav-portaled and
  // filters-bar instances) until the trigger was clicked again. The `.remaining-filters` wrapper
  // supplies the positioning context the absolutely-positioned badge needs, matching the loaded
  // markup. No breakpoint-dependent class here: this wrapper is SSR'd on the fast page, so a class
  // keyed off the live breakpoint would mismatch hydration on a desktop-rendered narrow viewport.
  return (
    <div className="remaining-filters">
      {getTrigger(handleFirstClick, activeFilterCount > 0)}
      {activeFilterCount > 0 && (
        <Badge type="warning" className="remaining-filters__badge">
          {activeFilterCount}
        </Badge>
      )}
    </div>
  )
}

LazyRemainingFilters.propTypes = {
  locale: string,
  selectedFilters: object,
  subscriptionFlowEnabled: bool,
}

export default WithTriangleUiContext((context) => ({
  locale: context?.locale,
  selectedFilters: context?.selectedFilters,
  subscriptionFlowEnabled: context?.subscriptionFlowEnabled,
}))(LazyRemainingFilters)
